import React from 'react';
import propTypes from 'prop-types';
import classNames from 'classnames';
import { useAppSelector } from 'app/hooks';

// constants
import { applicationTypes } from 'constants/scripts';

// components
import ValueWithHeader from 'common/ValueWithHeader';
import ZoneContainer from 'edit/ZoneContainerConnect';

// util
import {
  getLocaleUnitSymbol,
  getProductRateUnitSymbol
} from 'util/unit';
import { getMessage } from 'util/locale';
import { getZonesSortingCriteria } from 'util/sort';
import isCustomScript from 'util/isCustom';

import { isMultiLayerZoneGenerationEnabled } from 'manual/selectors';

const baseString = 'vra-zones-container';

const classString = classNames({
  [`${baseString}`]: true,
  'vra-fertility-zones-container': true
});
let FertilityZones = ({
  activeZoneId,
  fertilizerFormat,
  script,
  scriptTypeIsNitrogen,
  scriptType,
  zoneRef,
  canSortZonesByProperties
}) => { // NOSONAR False-Positive Case statement
  const isMultiLayerEnabled = useAppSelector(isMultiLayerZoneGenerationEnabled);
  
  if (!script) {
    return null;
  }

  const isCustom = isCustomScript(script);

  const isCustomLiquidNitrogen = isCustom && scriptTypeIsNitrogen
    && script.meta.products[0].matterState  === 'liquid';

  const { zones } = script;
  const sortZonesBy = getZonesSortingCriteria(canSortZonesByProperties, isMultiLayerEnabled);
  
  const zoneContainers = Object.values(zones)
  .sort(sortZonesBy)
  .map((zone, index) => {
    return (
      <ZoneContainer
        activeZoneId={activeZoneId}
        index={index}
        isCustom={isCustom}
        key={zone.meta.zoneId}
        script={script}
        scriptType={scriptType}
        scriptTypeIsNitrogen={scriptTypeIsNitrogen && !isCustomLiquidNitrogen}
        zone={zone}
        zoneId={zone.meta.zoneId}
        zoneRef={zoneRef}
      />
    );
  });

  const fertilizerRateLabel = getProductRateUnitSymbol(applicationTypes.FERTILIZER, fertilizerFormat);
  const nitrogenRateLabel = getLocaleUnitSymbol('area-density.application');
  const rateKey = scriptTypeIsNitrogen ? 'nitrogen' : 'fertilizer';
  const rateLabel = scriptTypeIsNitrogen ? nitrogenRateLabel : fertilizerRateLabel;
  const baseTitleMessage = scriptType === applicationTypes.APPLICATION ? 'title-text__protection' : 'title-text';

  return (
    <section className={classString} data-test-id="fertility-zones-container">
      <table>
        <thead>
          <tr>
            <th className={`${baseString}__table-head--zone-identifier`} />
            <th className={`${baseString}__table-head--name`}>{getMessage('zone')}</th>
            {
              (!scriptTypeIsNitrogen || !isCustom)
              && (
                <th className={`${baseString}__table-head--${rateKey}-rate`}>
                  <ValueWithHeader
                    header={rateLabel}
                    value={getMessage(`${baseTitleMessage}__${rateKey}-rate`)}
                  />
                </th>
              )
            }
            {
              scriptTypeIsNitrogen
              && (
                <th className={`${baseString}__table-head--fertilizer-rate`}>
                  <ValueWithHeader
                    header={fertilizerRateLabel}
                    value={getMessage(`${baseTitleMessage}__fertilizer-rate`)}
                  />
                </th>
              )
            }
          </tr>
        </thead>
        {zoneContainers}
      </table>
    </section>
  );
};

FertilityZones.propTypes = {
  activeZoneId: propTypes.string.isRequired,
  fertilizerFormat: propTypes.string.isRequired,
  script: propTypes.object,
  scriptType: propTypes.string.isRequired,
  scriptTypeIsNitrogen: propTypes.bool.isRequired,
  zoneRef: propTypes.func.isRequired,
  canSortZonesByProperties: propTypes.bool
};

export default FertilityZones;
